import { faCircleInfo, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse, Tooltip } from "antd";
import type React from "react";
import { useState } from "react";
import styled from "styled-components";
import {
  ENCODING_FORMAT_OPTIONS,
  TEMPORAL_RESOLUTION_OPTIONS,
} from "../../constants/formEnums";
import type { Interface } from "../../types/formTypes";
import BooleanField from "./BooleanField";
import MultiSelectField from "./MultiSelectField";
import TagInputField from "./TagInputField";
import TextField from "./TextField";

const FieldWrapper = styled.div`
  margin-bottom: 24px;
  text-align: left;
`;

const LabelRow = styled.label`
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-weight: 700;
  font-size: 16px;
  cursor: text;
  text-align: left;
`;

const RequiredMark = styled.span`
  color: #d32f2f;
`;

const InterfaceList = styled.div`
  margin-top: 6px;
`;

const StyledCollapse = styled(Collapse)`
  .ant-collapse-item {
    margin-bottom: 8px;
  }
  .ant-collapse-header {
    align-items: center !important;
  }
  .ant-collapse-header button {
    width: auto !important;
    min-width: unset !important;
    flex: 0 0 auto !important;
    background: none !important;
    border: none !important;
    padding: 4px 8px !important;
  }
  .ant-collapse-content-box {
    padding: 12px 16px !important;
    width: 100%;
    box-sizing: border-box;
  }
`;

const InterfaceFieldsWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;

  input,
  .ant-select {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
`;

const RemoveButton = styled.button`
  flex: 0 0 auto !important;
  background: none !important;
  border: none !important;
  cursor: pointer;
  padding: 4px 8px !important;
  color: var(--color-primary-desat) !important;
  font-size: 0.85rem;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: auto !important;
  min-width: auto !important;
  margin-left: auto;

  &:hover {
    color: #d32f2f !important;
  }
`;

const AddButton = styled.button`
  flex: 0 0 auto !important;
  margin-top: 12px;
  padding: 10px 40px !important;
  font-size: 0.9rem;
  border-radius: 6px;
  border: 1px dashed var(--color-primary-desat) !important;
  background: transparent !important;
  color: var(--color-primary) !important;
  cursor: pointer;

  &:hover {
    border-color: var(--color-primary) !important;
    background: rgba(0, 0, 0, 0.04) !important;
  }
`;

export interface InterfaceFieldProps {
  label: string;
  name: string;
  value: Interface[];
  onChange: (value: Interface[]) => void;
  required?: boolean;
  tooltip?: string;
  /** Label for each item in the list, e.g. "Interface" */
  itemLabel?: string;
  /** Label for the add button, e.g. "+ Add Interface" */
  addButtonLabel?: string;
}

const emptyInterface: Interface = {
  identifier: "",
  usedStandard: [],
  example: [],
  description: "",
  encodingFormat: [],
  hasTemporalResolution: [],
  required: undefined,
};

const InterfaceField: React.FC<InterfaceFieldProps> = ({
  label,
  name,
  value,
  onChange,
  required = false,
  tooltip,
  itemLabel = "Interface",
  addButtonLabel = "+ Add Interface",
}) => {
  const initialItems =
    value.length > 0 ? value : required ? [emptyInterface] : [];
  const [activeKeys, setActiveKeys] = useState<string[]>(() =>
    initialItems.map((_, i) => String(i)),
  );

  const items = value.length > 0 ? value : required ? [emptyInterface] : [];

  const updateItem = (index: number, updates: Partial<Interface>) => {
    const next = [...items];
    next[index] = { ...next[index], ...updates };
    onChange(next);
  };

  const addItem = () => {
    const next = [...items, { ...emptyInterface }];
    onChange(next);
    setActiveKeys((prev) => [...prev, String(next.length - 1)]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1 && required) return;
    const next = items.filter((_, i) => i !== index);
    onChange(next);
    setActiveKeys((prev) =>
      prev
        .filter((k) => k !== String(index))
        .map((k) =>
          parseInt(k, 10) > index ? String(parseInt(k, 10) - 1) : k,
        ),
    );
  };

  const collapseItems = items.map((item, index) => ({
    key: String(index),
    label: (
      <PanelHeader>
        <span>{itemLabel}</span>
        {(items.length > 1 || !required) && items.length >= 1 && (
          <RemoveButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeItem(index);
            }}
            aria-label={`Remove ${itemLabel.toLowerCase()}`}
          >
            <FontAwesomeIcon icon={faTrash} />
          </RemoveButton>
        )}
      </PanelHeader>
    ),
    children: (
      <InterfaceFieldsWrapper>
        <TextField
          label="Identifier"
          name={`${name}-${index}-identifier`}
          value={item.identifier ?? ""}
          onChange={(v) => updateItem(index, { identifier: v })}
          tooltip="The identifier property represents any kind of identifier for any kind of Thing, such as ISBNs, GTIN codes, UUIDs etc. Schema.org provides dedicated properties for representing many of these, either as Textual strings or as URL (URI) links. See background notes for more details."
        />
        <TagInputField
          label="Used Standard"
          name={`${name}-${index}-usedStandard`}
          value={Array.isArray(item.usedStandard) ? item.usedStandard : []}
          onChange={(v) => updateItem(index, { usedStandard: v })}
          tooltip="Supported standards of the API."
        />
        <TagInputField
          label="Example"
          name={`${name}-${index}-example`}
          value={Array.isArray(item.example) ? item.example : []}
          onChange={(v) => updateItem(index, { example: v })}
          tooltip="Example for the interface."
        />
        <TextField
          label="Description"
          name={`${name}-${index}-description`}
          value={item.description ?? ""}
          onChange={(v) => updateItem(index, { description: v })}
          tooltip="Description of the content exchanged via the interface."
        />
        <MultiSelectField
          label="Encoding Format(s)"
          name={`${name}-${index}-encodingFormat`}
          value={Array.isArray(item.encodingFormat) ? item.encodingFormat : []}
          onChange={(v) => updateItem(index, { encodingFormat: v })}
          options={[...ENCODING_FORMAT_OPTIONS]}
          tooltip="Description of the allowed encoding format of the interface, e.g., csv"
        />
        <MultiSelectField
          label="Temporal Resolution"
          name={`${name}-${index}-hasTemporalResolution`}
          value={
            Array.isArray(item.hasTemporalResolution)
              ? item.hasTemporalResolution
              : []
          }
          onChange={(v) => updateItem(index, { hasTemporalResolution: v })}
          options={[...TEMPORAL_RESOLUTION_OPTIONS]}
          tooltip="Description on the required temporal resolution for the interface."
        />
        <BooleanField
          label="Is Required"
          name={`${name}-${index}-required`}
          value={item.required}
          onChange={(v) => updateItem(index, { required: v })}
          tooltip="Is it required to provide input to the interface?"
        />
      </InterfaceFieldsWrapper>
    ),
  }));

  return (
    <FieldWrapper>
      <LabelRow htmlFor={name}>
        {label}
        {required && <RequiredMark>*</RequiredMark>}
        {tooltip && (
          <Tooltip title={tooltip}>
            <span style={{ display: "inline-flex", cursor: "help" }}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                style={{
                  color: "var(--color-primary-desat)",
                  fontSize: "0.9rem",
                }}
              />
            </span>
          </Tooltip>
        )}
      </LabelRow>
      <InterfaceList>
        <StyledCollapse
          activeKey={activeKeys}
          onChange={(keys) =>
            setActiveKeys(Array.isArray(keys) ? keys : [keys])
          }
          items={collapseItems}
        />
        <AddButton type="button" onClick={addItem}>
          {addButtonLabel}
        </AddButton>
      </InterfaceList>
    </FieldWrapper>
  );
};

export default InterfaceField;
